import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import '../css/AddUnit.css';

const AddUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEdit = Boolean(id);
  const userData = location.state?.user || null;
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    phone: '',
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Reset form data when component mounts or when switching between add/edit modes
  useEffect(() => {
    if (isEdit && userData) {
      // Edit mode with user data
      setFormData({
        name: userData.name || '',
        type: userData.type || '',
        phone: userData.phone || '',
        email: userData.email || '',
        password: ''
      });
    } else if (!isEdit) {
      // Add mode - reset to empty form
      setFormData({
        name: '',
        type: '',
        phone: '',
        email: '',
        password: ''
      });
    }
  }, [isEdit, userData]);

  const userTypes = [
    { value: '', label: 'Select User Type' },
    { value: 'Cashier', label: 'Cashier' },
    { value: 'Waiter', label: 'Waiter' },
    { value: 'Safe', label: 'Safe' }
  ];

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      showToast('Please enter a name', 'error');
      return false;
    }
    if (!formData.type) {
      showToast('Please select a user type', 'error');
      return false;
    }
    if (!formData.phone.trim()) {
      showToast('Please enter a phone number', 'error');
      return false;
    }
    if (!formData.email.trim()) {
      showToast('Please enter an email', 'error');
      return false;
    }
    if (!isEdit) {
      if (!formData.password.trim()) {
        showToast('Please enter a password', 'error');
        return false;
      }
      if (formData.password.length < 6) {
        showToast('Password must be at least 6 characters', 'error');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.milstudio.in';
    let response;
    let data;
    try {
      if (isEdit) {
        response = await fetch(`${apiUrl}/edit-stafflogin.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            staff_id: id,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            role_name: formData.type
          }),
        });
      } else {
        response = await fetch(`${apiUrl}/add-stafflogin.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            password: formData.password,
            role_name: formData.type
          }),
        });
      }
    } catch (fetchError) {
      showToast('Network error: ' + fetchError.message, 'error');
      setIsSubmitting(false);
      return;
    }
    try {
      data = await response.json();
    } catch (jsonError) {
      showToast('Invalid server response.', 'error');
      setIsSubmitting(false);
      return;
    }
    if (data.status) {
      showToast(`User "${formData.name}" ${isEdit ? 'updated' : 'added'} successfully!`, 'success');
      setTimeout(() => {
        navigate('/user-list');
      }, 1500);
    } else {
      showToast(data.message || `Error ${isEdit ? 'updating' : 'adding'} user. Please try again.`, 'error');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="add-user-content">
      <div className="page-header" style={{display: 'block'}}>
        <h1>{isEdit ? 'Edit User' : 'Add User'}</h1>
        <p>{isEdit ? 'Edit user details' : 'Create a new user account'}</p>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter full name"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="type">User Type</label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
            >
              {userTypes.map((type, index) => (
                <option key={index} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              required
              disabled={isSubmitting}
            />
          </div>

          {!isEdit && (
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter password (min 6 characters)"
                required
                disabled={isSubmitting}
                minLength="6"
              />
            </div>
          )}

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (isEdit ? 'Updating...' : 'Adding User...') : (isEdit ? 'Update User' : 'Add User')}
          </button>
        </form>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddUser; 